<template>
  <n-menu ref="menu" @changeVisibility="onChangeVisibilityMenu">
    <n-menu-group :subheader="$t('signatureList.selectAnAssignee')" divider>
      <div class="capped-height" :class="{ 'is-saving': saving }" :aria-busy="saving ? 'true' : null">
        <n-scroller class="n-scroller">
          <n-list-item v-for="user in assignableUsers" :key="user.id" @click="onAssignUser(user.id)">
            <div class="assignee-wrapper">
              <UserAvatar :user="user" size="xs" />
              <p>{{ userFullName(user) }}</p>
              <span v-if="saving && pendingUserId === user.id" class="assignee-loading">
                <n-loading size="s" dark />
              </span>
            </div>
          </n-list-item>
        </n-scroller>
      </div>
    </n-menu-group>
    <n-menu-group v-if="hasAssignedRecipient">
      <n-list-item
        :class="{ 'is-saving': saving }"
        :leadingIcon="saving && !pendingUserId ? undefined : 'trash'"
        :text="$t('signatureList.deleteRecipient')"
        @click="onDeleteRecipient"
      >
        <template #action>
          <span v-if="saving && !pendingUserId" class="assignee-loading"><n-loading size="s" dark /></span>
        </template>
      </n-list-item>
    </n-menu-group>
    <n-menu-group v-if="allowAddRecipient">
      <n-list-item leadingIcon="user-add" :text="$t('signatureList.addNewRecipient')" @click="openAddRecipientModal" />
    </n-menu-group>
    <n-modal-background v-if="showAddRecipientModal" @close="showAddRecipientModal = false">
      <AddRecipient
        @close="showAddRecipientModal = false"
        @back="showAddRecipientModal = false"
        :noBackButton="true"
        @select="onAssignUser"
      />
    </n-modal-background>
    <n-menu-group v-if="!hasAssignedRecipient">
      <div class="assign-all-toggle" @click.stop>
        <span class="assign-all-label">{{ $t('signatureList.mapRecipientToAllUnassigned') }}</span>
        <n-toggle class="assign-all-switch" :value="assignAllUnassigned" size="s" @change="onToggleAssignAll" />
      </div>
    </n-menu-group>
  </n-menu>
</template>

<script lang="ts">
import NMenu from '@/modules/global/components/core/NMenu/NMenu.vue'
import NToggle from '@/modules/global/components/core/NToggle.vue'
import UserAvatar from '@/modules/global/components/User/UserAvatar.vue'
import AddRecipient from '@/modules/space/components/ShareSpace/AddRecipient.vue'
import Space from '@/modules/space/models/Space'
import { useSpaceStore } from '@/modules/space/stores/SpaceStore'
import User, { UserState } from '@/modules/user/models/User'
import { useUserStore } from '@/modules/user/stores/UserStore'
import { mapState, mapStores } from 'pinia'
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import { useSpaceVariableStore } from '@/modules/space/stores/SpaceVariableStore'
import SpaceVariable from '@/modules/space/models/SpaceVariable'

@Component({
  components: {
    NToggle,
    UserAvatar,
    AddRecipient
  },
  computed: {
    ...mapState(useSpaceStore, ['currentSpace']),
    ...mapState(useUserStore, ['users', 'currentTeamMembers']),
    ...mapStores(useSpaceVariableStore)
  }
})
export default class VariableAssignmentMenu extends Vue {
  @Prop() variableId!: string
  @Prop() variableName!: string
  @Prop() spaceId!: string
  /** A save is in flight: rows stop reacting and the pending row shows a spinner */
  @Prop({ type: Boolean, default: false }) saving!: boolean
  /** Templates hold no real recipients (and have no space to add one to), so they hide the entry */
  @Prop({ type: Boolean, default: true }) allowAddRecipient!: boolean
  /** The person being assigned; empty while a recipient is being removed */
  @Prop({ type: String }) pendingUserId?: string

  @Ref('menu') menuRef!: NMenu

  private users!: User[]
  private currentSpace!: Space
  private currentTeamMembers!: User[]
  private spaceVariableStore!: ReturnType<typeof useSpaceVariableStore>

  private showAddRecipientModal = false
  private assignAllUnassigned = false

  get assignableUsers(): User[] {
    // Get assigned recipient ID from variable if available
    const variables = this.spaceVariableStore.getVariablesBySpaceId(this.spaceId)
    const variable = variables?.find(
      (v: SpaceVariable) =>
        (this.variableId && v.id === this.variableId) || (this.variableName && v.variableName === this.variableName)
    )
    const assignedUserId = variable?.assignedToRecipientId

    // Get recipients (excluding assigned user)
    const recipients = this.currentSpace.recipients
      .map(r => this.users.find(u => u.id === r.userId))
      .filter((user): user is User => user !== undefined && user.id !== assignedUserId)

    // Get team members (excluding assigned user and disabled users)
    const teamMembers = this.currentTeamMembers.filter(
      user => user.id !== assignedUserId && user.state !== UserState.DISABLED
    )

    // Combine and deduplicate
    const assignableUsers = new Set([...recipients, ...teamMembers])
    return Array.from(assignableUsers)
  }

  get hasAssignedRecipient(): boolean {
    const variables = this.spaceVariableStore.getVariablesBySpaceId(this.spaceId)
    const variable = variables?.find(
      (v: SpaceVariable) =>
        (this.variableId && v.id === this.variableId) || (this.variableName && v.variableName === this.variableName)
    )
    return !!variable?.assignedToRecipientId
  }

  onAssignUser(userId: string) {
    if (this.saving) return
    this.$emit('onAssignUser', { userId, assignAllUnassigned: this.assignAllUnassigned })
  }

  onDeleteRecipient() {
    if (this.saving) return
    this.$emit('onDeleteRecipient')
  }

  onToggleAssignAll(value: boolean) {
    this.assignAllUnassigned = value
  }

  userFullName(user: User): string {
    const { firstName, lastName, email } = user

    return firstName && lastName ? `${firstName} ${lastName}` : email
  }

  openAddRecipientModal() {
    if (this.saving) return
    // The menu no longer closes on every click, so the add-recipient entry hides it itself
    this.menuRef.close()
    this.showAddRecipientModal = true
  }

  close() {
    this.menuRef.close()
  }

  open(openFrom: HTMLElement) {
    // NMenu toggles when asked to open from the element it is already open on; the click on a callout means "show
    // me the menu", also when it is still open from before a scroll (and so out of sight), so close it first
    this.menuRef.close()
    this.menuRef.open(openFrom)
  }

  onChangeVisibilityMenu(visible: boolean) {
    this.$emit('onChangeVisibilityMenu', visible)
  }
}
</script>

<style scoped lang="scss">
@use '@/assets/styles' as *;

.is-saving {
  opacity: 0.6;
  pointer-events: none;
}

.assignee-loading {
  // NLoading centers itself absolutely, so it needs a box of its own to sit in
  position: relative;
  flex-shrink: 0;
  width: $notch-space-3x;
  height: $notch-space-3x;
  margin-left: auto;
}

.assignee-wrapper {
  display: flex;
  gap: $notch-space-2x;
}

.capped-height {
  display: flex;
  flex-direction: column;
  max-height: $notch-space-60x;
  max-width: 100%;
  width: 100%;
  overflow: hidden;

  .n-scroller {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
  }
}

.assign-all-toggle {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: $notch-space-2x;
  margin-top: 5px;
  padding: $notch-space-2x $notch-space-3x;
  background-color: $notch-color-neutral-sand-100;
  border-radius: $notch-radius-2x;
}

.assign-all-label {
  @include notch-text-paragraph-regular-S;
  color: $notch-color-neutral-grey-900;
}

.assign-all-switch ::v-deep label {
  background-color: $notch-color-neutral-sand-200;
  border-color: $notch-color-neutral-sand-200;
}

.assign-all-switch ::v-deep label.active {
  background-color: $notch-color-semantic-info-500;
  border-color: $notch-color-semantic-info-500;
}
</style>
